import { PAGES } from '$lib/utils/constants';

/**
 * Query string keys used by login URL flows (session banners, return-after-login).
 */
export const LOGIN_URL_QUERY_KEYS = {
	ERROR_TYPE: 'error_type',
	REDIRECT_TO: 'redirect_to',
} as const;

/**
 * Allowed values for the `error_type` query parameter on the login route.
 */
export const LOGIN_ERROR_TYPES = {
	REDIRECT_LOOP: 'redirect_loop',
	TOKEN_EXPIRED: 'token_expired',
} as const;

export type LoginErrorTypeValue = (typeof LOGIN_ERROR_TYPES)[keyof typeof LOGIN_ERROR_TYPES];

/**
 * HTML/plain copy for `ComponentError` / `ComponentErrorRebrand` when `error_type` is set on the URL.
 * Keys match `LOGIN_ERROR_TYPES` string values. Re-exported on each login variant page config.
 */
export const errorTypeMessages: Record<string, string> = {
	[LOGIN_ERROR_TYPES.REDIRECT_LOOP]: `<strong>Something went wrong with your session.</strong> We ran into a hiccup and had to log you out. Our Customer Care team is ready to help — <a href="${PAGES.EXTERNAL.CUSTOMER_SUPPORT_PLUS_CONTACT_US}" class="text-electricBlue underline" target="_blank" rel="noopener noreferrer" data-track-id="login:session-issue-contact">contact us here</a> and we'll get things sorted out quickly.`,
	[LOGIN_ERROR_TYPES.TOKEN_EXPIRED]: 'Something went wrong. Please log in again.',
};

export type LoginErrorBannerPayload = { message: string };

/**
 * Builds the banner payload for `ComponentError` / `ComponentErrorRebrand` from the current query string.
 * @param {URLSearchParams} searchParams - Typically `window.location` or `$page.url` search params.
 * @param {Record<string, string>} messages - Map keyed by `error_type` value; defaults to shared `errorTypeMessages`.
 * @returns {LoginErrorBannerPayload | null} A `{ message }` object when `error_type` matches a known key, otherwise `null`.
 */
export function getMessageForErrorType(
	searchParams: URLSearchParams,
	messages: Record<string, string> = errorTypeMessages
): LoginErrorBannerPayload | null {
	const type = searchParams.get(LOGIN_URL_QUERY_KEYS.ERROR_TYPE);
	if (!type) return null;

	const message = messages[type];
	if (!message) return null;

	return { message };
}

/**
 * Returns a same-origin app path safe to pass to `goto` after login. Rejects external URLs and paths outside `/abc`.
 * @param {string | null | undefined} raw - Raw `redirect_to` (or similar) query value, possibly encoded.
 * @returns {string | null} Pathname + search + hash starting with `/abc`, or `null` if unsafe or invalid.
 */
export function getSanitizedPath(raw: string | null | undefined): string | null {
	if (raw == null || typeof raw !== 'string') return null;
	let decoded: string;
	try {
		decoded = decodeURIComponent(raw.trim());
	} catch {
		return null;
	}
	if (!decoded || typeof window === 'undefined') return null;

	let pathname: string;
	let search = '';
	let hash = '';
	try {
		if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
			const u = new URL(decoded);
			if (u.origin !== window.location.origin) return null;
			pathname = u.pathname;
			search = u.search;
			hash = u.hash;
		} else {
			const u = new URL(decoded, window.location.origin);
			pathname = u.pathname;
			search = u.search;
			hash = u.hash;
		}
	} catch {
		return null;
	}

	if (!pathname.startsWith('/abc')) return null;
	if (pathname.includes('//')) return null;

	return `${pathname}${search}${hash}`;
}

/**
 * When `error_type` is `token_expired` and `redirect_to` sanitizes to an internal path, returns that path for post-login navigation.
 * @param {URLSearchParams} searchParams - Current login URL search params.
 * @returns {string | null} The sanitized internal app path if `error_type` is `token_expired` and `redirect_to` sanitizes to an internal path, otherwise `null`.
 */
export function getURLRedirectPathFromQuery(searchParams: URLSearchParams): string | null {
	return getSanitizedPath(searchParams.get(LOGIN_URL_QUERY_KEYS.REDIRECT_TO));
}
